/**
## Field Text Area

`SqFieldTextAreaComponent` is a variation on a classic Ember TextArea field. While technically
the input is standalone, it's really intended to be nested inside a Form Field block component.
The two components communicate with each other over event handling. By default, each text area
has a default height. If you'd like for the text area to automatically shrink and grow to match
text content, set parameter `autoSize` to `true`.

Parameters to the component can follow any standard Ember TextArea helper.
Not really anything proprietary or new here.

```example
@title Basic Example in Form Field

@controller-properties
{
  status: 'Lorem ipsum dolor sit amet, consectetur adipisicing elit. Minus possimus beatae quos, illum ipsam ipsum in ipsa voluptate eum repudiandae, eos excepturi sint, nemo odit iste sequi eaque deleniti. Quas.'
}

@hbs
<div class="form-table">
  <div class="form-row">
    {{#sq-form-field label=(t "common.status")}}
      {{sq-field-textarea value=status
                          placeholder="Sample Placeholder"}}
    {{/sq-form-field}}
  </div>
</div>
```

```example
@title Auto Sizing

@controller-properties
{
  status: 'Lorem ipsum dolor sit amet, consectetur adipisicing elit. Animi reiciendis enim doloremque consequatur ipsam iste eum laudantium. Libero deleniti ipsa maxime aliquam neque ea illo, perspiciatis, facere molestias iure aut!'
}

@hbs
<div class="form-table">
  <div class="form-row">
    {{#sq-form-field label=(t "common.status")}}
      {{sq-field-textarea value=status
                          autoSize=true}}
    {{/sq-form-field}}
  </div>
</div>
```


### Parameters (Common)
@param {String} value - The value attribute of the text area element. As the user inputs text, this property is automatically updated.
@param {String} placeholder - Placeholder text. When not defined, it tries to pull the associated wrapper label.
@param {Boolean} [autoSize=false] - If `true`, the text area will auto grow and shrink in vertical size to accomodate the current value.

Sass dependencies at: `app/assets/stylesheets/bridgeport/components/form-elements/formfield/_formfield-textarea.sass`
*/

import { scheduleOnce } from '@ember/runloop';
import { observer } from '@ember/object';
import { on } from '@ember/object/evented';
import { TextArea } from '@ember/legacy-built-in-components';
import FieldElementSupport from '../utils/field-element-support';
const MINIMUM_TEXTAREA_HEIGHT = 47;

// eslint-disable-next-line ember/no-classic-classes
export default TextArea.extend(FieldElementSupport, {
  autofocus: false,
  autoSize: false,
  classNames: 'form-field__textarea',
  classNameBindings: ['autoSize:form-field__textarea--autosize'],
  disabled: false,
  minHeight: MINIMUM_TEXTAREA_HEIGHT,
  // eslint-disable-next-line ember/no-on-calls-in-components
  runCaretAlignment: on('init',
  // eslint-disable-next-line ember/no-observers
  observer('value', function () {
    if (this.get('autoSize')) {
      scheduleOnce('afterRender', this, this.resizeTextAreaToContent);
    }
  })),
  resizeTextAreaToContent() {
    if (this.get('state') === 'preRender' || this.get('state') === 'destroying') {
      return;
    }
    const minHeight = this.get('minHeight');
    this.get('element').style.height = `${minHeight}px`;
    const contentHeight = this.get('element').scrollHeight;
    if (contentHeight > minHeight) {
      this.get('element').style.height = `${contentHeight}px`;
    }
  }
});